import { ProductInfo, ProductVariant } from "./../types/AppTypes";
import { useState, useCallback, useEffect, useRef } from "react"; // Add useRef
import { useProductInfos } from "./useProductInfos";

const useCustomPaginatedProducts = ({
  limit,
  infoVariablesP,
  skip,
  setProductsLoading,
  dispatch,
  setEnrichedProductList,
  setLocalLoading,
}: {
  limit?: number;
  infoVariablesP?: any;
  skip?: boolean;
  setProductsLoading?: (loading: boolean) => void;
  dispatch?: any;
  setEnrichedProductList?: (products: ProductInfo[]) => void;
  setLocalLoading?: (loading: boolean) => void;
}) => {
  const [productsForDisplay, setProductsForDisplay] = useState<any[]>([]);
  const [enrichedProducts, setEnrichedProducts] = useState<ProductInfo[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      if (setProductsLoading && dispatch) {
        dispatch(setProductsLoading(loading));
      }
      if (setLocalLoading) {
        setLocalLoading(loading);
      }
    },
    //eslint-disable-next-line
    [setProductsLoading, dispatch]
  );

  const productInfos = useProductInfos({
    limit,
    infoVariablesP,
    abortControllerRef,
  });

  const loading = productInfos?.loading;

  const loadPage = useCallback(
    async (token: string | null = null) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        handleLoadingChange(true);
        const productInfosResult = await productInfos?.loadPage(
          token,
          abortControllerRef.current.signal
        );

        if (abortControllerRef.current.signal.aborted) return;

        if (!productInfosResult?.length) {
          setEnrichedProducts([]);
          return;
        }

        if (dispatch && setEnrichedProductList) {
          dispatch(setEnrichedProductList(productInfosResult));
        }
        const defaultProducts = getDefaultParseVariants(productInfosResult);

        setProductsForDisplay(defaultProducts);

        setEnrichedProducts(productInfosResult);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Error loading page:", error);
        }
      } finally {
        handleLoadingChange(false);
        productInfos?.setLoading(false);
      }
    },
    //eslint-disable-next-line
    [productInfos.loadPage]
  );

  useEffect(() => {
    if (!skip) {
      loadPage();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [skip, loadPage]);

  return {
    products: productInfos?.products,
    loading,
    hasNextPage: productInfos?.hasNextPage,
    loadNextPage: async () => {
      await productInfos.loadNextPage();
      await loadPage(productInfos?.nextToken);
    },
    loadPreviousPage: async () => {
      await productInfos?.loadPreviousPage();
      await loadPage();
    },
    loadPage,
    enrichedProducts,
    productsForDisplay,
  };
};

export default useCustomPaginatedProducts;
export const getDefaultParseVariants = (productInfos: ProductInfo[] = []) => {
  if (!productInfos || productInfos.length === 0) return [];

  return productInfos
    .map((product) => {
      const variants = (product?.variants || [])
        .map((v: any) => {
          try {
            return typeof v === "string" ? JSON.parse(v) : v;
          } catch (e) {
            console.error("Failed to parse variant JSON:", v);
            return null;
          }
        })
        .filter(Boolean);

      const defaultVariants = variants
        .filter((variant) => variant?.show_by_default === true)
        .map((variant) => ({
          ...variant,
          product_id: product?.id,
          seller_id: product?.seller_id,
          brand_id: product?.brand_id,
          category_id: product?.category_id,
          ...(variant?.name ? {} : { name: product?.name }),
        }));

      if (defaultVariants.length > 0) return defaultVariants;

      return variants.length > 0
        ? [
            {
              ...variants?.[0],
              product_id: product?.id,
              seller_id: product?.seller_id,
              brand_id: product?.brand_id,
              category_id: product?.category_id,
              ...(variants[0]?.name ? {} : { name: product?.name }),
            },
          ]
        : [];
    })
    .flat();
};
